import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import Dashboard from './Dashboard/Dashboard';
import MainLayout from './Layouts/MainLayout';
import { AuthProvider, useAuth } from './stateManagers/services/AuthContext';
import { LoadingScreen } from './components/LoadingScreen';
import LoginScreen from './Layouts/Authorisation/LoginScreen';
import InstituteProfile from './Layouts/MyProfile/InstituteProfile';
import Branches from './Layouts/Branches/Branches';
import BranchDetails from './Layouts/Branches/BranchDetails';
import BranchStudents from './Layouts/Branches/BranchStudents';
import { getPathForRoute } from './config/routeConfig';
import Courses from './Layouts/Courses/Courses';
import Subjects from './Layouts/Subjects/Subjects';
import StudyMaterials from './Layouts/Studymaterials/Studymaterials';
import BackupPanel from './Layouts/Beta/BackupPanel';
import { isBeta } from './stateManagers/apiSetup/env';

const AppContent: React.FC = () => {
  const { isAuthenticated, user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [contextData, setContextData] = useState<Record<string, any>>({});

  const handleNavigation = (routeId: string, params?: Record<string, string>, data?: Record<string, any>) => {
    if (data) setContextData(prev => ({ ...prev, ...data }));
    const path = getPathForRoute(routeId, params);
    navigate(path);
  };

  const handleNavigateTo = (path: string, data?: Record<string, any>) => {
    if (data) setContextData(prev => ({ ...prev, ...data }));
    navigate(path);
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleLogoutCancel = () => setShowLogoutModal(false);
  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setShowLogoutModal(false);
    }
  };

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <LoginScreen />;

  return (
    <>
      <MainLayout
        user={user}
        currentPath={location.pathname}
        onNavigate={handleNavigation}
        onLogout={handleLogoutClick}
        contextData={contextData}
      >
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/institute-profile" element={<InstituteProfile />} />

          {/* Branches */}
          <Route
            path="/branches"
            element={<Branches onBranchClick={(id) => handleNavigateTo(`/branches/${id}`)} />}
          />
          <Route
            path="/branches/:branchId"
            element={
              <BranchDetailsWrapper
                onBack={handleNavigateBack}
                onBranchLoad={(name, instituteId) =>
                  setContextData(prev => ({ ...prev, branchName: name, instituteId }))
                }                onViewStudents={(branchId) => handleNavigateTo(`/branches/${branchId}/students`)}
              />
            }
          />
          {/* ← NEW: branch students listing */}
          <Route
            path="/branches/:branchId/students"
            element={
              <BranchStudentsWrapper
                onBack={handleNavigateBack}
                branchName={contextData.branchName}
                instituteId={contextData.instituteId}
              />
            }
          />

          <Route path="/courses-and-subjects/courses" element={<Courses />} />
          <Route path="/courses-and-subjects/subjects" element={<Subjects />} />
          <Route path="/study-materials" element={<StudyMaterials />} />
          <Route path="/school-setup/database" element={<div style={{ padding: '40px' }}><h2>Database Setup Component</h2></div>} />

          {isBeta && <Route path="/backup-panel" element={<BackupPanel />} />}

          <Route path="*" element={
            <div style={{ padding: '40px' }}>
              <h2 style={{ fontSize: '1.8rem', fontWeight: '600', color: '#1a1a1a', marginBottom: '16px' }}>
                Welcome, {user?.email}!
              </h2>
              <p style={{ color: '#666', fontSize: '1rem' }}>
                Select a menu item to get started.
              </p>
            </div>
          } />
        </Routes>
      </MainLayout>

      {showLogoutModal && (
        <LogoutModal onConfirm={handleLogoutConfirm} onCancel={handleLogoutCancel} />
      )}
    </>
  );
};

// ── Logout modal ──────────────────────────────────────────────────────────────

const LogoutModal: React.FC<{ onConfirm: () => void; onCancel: () => void }> = ({ onConfirm, onCancel }) => (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
  }}>
    <div style={{
      background: 'white', borderRadius: '16px', padding: '32px',
      maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
    }}>
      <div style={{
        width: '56px', height: '56px', background: '#fff3cd', borderRadius: '12px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px'
      }}>
        <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.5rem', color: '#856404' }}></i>
      </div>
      <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px 0' }}>Confirm Logout</h3>
      <p style={{ fontSize: '1rem', color: '#666', margin: '0 0 24px 0', lineHeight: '1.5' }}>
        Are you sure you want to logout? You'll need to login again to access your dashboard.
      </p>
      <div style={{ display: 'flex', gap: '12px' }}>
        <button onClick={onCancel}
          style={{ flex: 1, padding: '14px 24px', background: '#f5f5f5', color: '#333', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = '#e0e0e0'}
          onMouseLeave={e => e.currentTarget.style.background = '#f5f5f5'}
        >Cancel</button>
        <button onClick={onConfirm}
          style={{ flex: 1, padding: '14px 24px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = '#c82333'}
          onMouseLeave={e => e.currentTarget.style.background = '#dc3545'}
        >Logout</button>
      </div>
    </div>
  </div>
);

// ── Route wrappers ────────────────────────────────────────────────────────────

const BranchDetailsWrapper: React.FC<{
  onBack: () => void;
  onBranchLoad: (name: string, instituteId: string) => void;
  onViewStudents: (branchId: string) => void;
}> = ({ onBack, onBranchLoad, onViewStudents }) => {
  const { branchId } = useParams<{ branchId: string }>();
  if (!branchId) return null;
  return (
    <BranchDetails
      branchId={branchId}
      onBack={onBack}
      onBranchLoad={onBranchLoad}
      onViewStudents={() => onViewStudents(branchId)}
    />
  );
};

const BranchStudentsWrapper: React.FC<{
  onBack: () => void;
  branchName?: string;
  instituteId?: string;
}> = ({ onBack, branchName, instituteId }) => {
  const { branchId } = useParams<{ branchId: string }>();
  if (!branchId) return null;
  return (
    <BranchStudents
      branchId={branchId}
      instituteId={instituteId || ''}
      branchName={branchName || 'Branch'}
      onBack={onBack}
    />
  );
};

// ── Root ──────────────────────────────────────────────────────────────────────

const App: React.FC = () => (
  <AuthProvider>
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  </AuthProvider>
);

export default App;